import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, Gift, Check, Trophy } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  userId: string;
}

const REWARD_THRESHOLD = 10;

const generateCode = (userId: string) =>
  (userId.replace(/-/g, "").slice(0, 6) + Math.floor(Math.random() * 100)).toUpperCase();

const ReferralCard = ({ userId }: Props) => {
  const [code, setCode] = useState<string | null>(null);
  const [count, setCount] = useState(0);
  const [rewardStatus, setRewardStatus] = useState<"none" | "pending" | "approved" | "rejected">("none");
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const load = async () => {
      const { data: existing } = await (supabase as any)
        .from("referral_codes")
        .select("code")
        .eq("user_id", userId)
        .maybeSingle();

      let myCode = existing?.code;
      if (!myCode) {
        myCode = generateCode(userId);
        await (supabase as any).from("referral_codes").insert({ user_id: userId, code: myCode });
      }
      setCode(myCode);

      // Only count referrals whose referred user has linked the Telegram bot
      const { data: refRows } = await (supabase as any)
        .from("referrals")
        .select("referred_user_id")
        .eq("referrer_user_id", userId);
      const referredIds = (refRows ?? []).map((r: any) => r.referred_user_id);
      let validCount = 0;
      if (referredIds.length > 0) {
        const { count: linkedCount } = await (supabase as any)
          .from("user_telegram")
          .select("user_id", { count: "exact", head: true })
          .in("user_id", referredIds);
        validCount = linkedCount || 0;
      }
      setCount(validCount);

      const { data: reward } = await (supabase as any)
        .from("referral_rewards")
        .select("id, status")
        .eq("user_id", userId)
        .eq("reward_type", "free_month")
        .maybeSingle();
      if (reward?.status === "approved") setRewardStatus("approved");
      else if (reward?.status === "pending") setRewardStatus("pending");
      else if (reward?.status === "rejected") setRewardStatus("rejected");
      else setRewardStatus("none");
    };
    load();
  }, [userId]);

  const link = code ? `https://fitawrari.com/auth?ref=${code}` : "";

  const handleCopy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast({ title: "Referral link copied!", description: "Share it with friends to earn 1 free month of Premium." });
    setTimeout(() => setCopied(false), 2000);
  };

  if (!code) return null;

  const progress = Math.min((count / REWARD_THRESHOLD) * 100, 100);
  const remaining = Math.max(REWARD_THRESHOLD - count, 0);

  const headline =
    rewardStatus === "approved"
      ? "🎁 You earned 1 FREE month of Premium!"
      : rewardStatus === "pending"
      ? "⏳ Reward submitted — awaiting admin approval"
      : rewardStatus === "rejected"
      ? "❌ Your last reward request was not approved"
      : `Invite friends • ${count}/${REWARD_THRESHOLD} signed up`;

  const subline =
    rewardStatus === "approved"
      ? "You've been enrolled in the Premium group."
      : rewardStatus === "pending"
      ? "Admin will verify your 10 Telegram-linked referrals shortly."
      : rewardStatus === "rejected"
      ? "Keep inviting friends — you can earn another reward."
      : remaining === 0
      ? "Reward unlocked — pending admin approval!"
      : `${remaining} more friend${remaining === 1 ? "" : "s"} (must connect Telegram bot) = 1 FREE month of Premium`;

  return (
    <Card className="mb-6 border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
            {rewardStatus === "approved" ? <Trophy className="w-5 h-5 text-amber-600" /> : <Gift className="w-5 h-5 text-amber-600" />}
          </div>
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-semibold">{headline}</p>
            <p className="text-xs text-muted-foreground">{subline}</p>
          </div>
          <Button size="sm" variant="outline" onClick={handleCopy}>
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>
        {rewardStatus === "none" && <Progress value={progress} className="h-1.5" />}
        <p className="text-xs text-muted-foreground truncate">{link}</p>
      </CardContent>
    </Card>
  );
};

export default ReferralCard;
